class ContinuousScroll extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.initialized = false;
    this.observer = null;
    this.abortController = null;
    this.onIntersection = this.onIntersection.bind(this);
    this.onLinkClick = this.onLinkClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.nextLink = this.querySelector('[data-continuous-scroll-next]');
    this.status = this.querySelector('[data-continuous-scroll-status]');
    this.grid = this.parentElement?.querySelector(this.dataset.gridSelector);

    if (!this.nextLink || !this.grid || typeof fetch !== 'function') return;

    this.initialized = true;
    this.nextLink.addEventListener('click', this.onLinkClick);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.onIntersection, {
        rootMargin: '0px 0px 400px 0px',
      });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.nextLink?.removeEventListener('click', this.onLinkClick);
    this.observer?.disconnect();
    this.observer = null;

    if (this.abortController) this.abortController.abort();
    this.abortController = null;
    this.initialized = false;
  }

  onIntersection(entries) {
    if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
  }

  onLinkClick(event) {
    event.preventDefault();
    this.loadNextPage();
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }

  async loadNextPage() {
    if (this.loading || !this.nextLink) return;

    const nextUrl = this.nextLink.getAttribute('href');
    if (!nextUrl) return;

    this.loading = true;
    this.nextLink.setAttribute('aria-disabled', 'true');
    this.setAttribute?.('aria-busy', 'true');
    this.setStatus(this.dataset.loadingText);
    this.observer?.unobserve(this);
    this.abortController = typeof AbortController === 'function' ? new AbortController() : null;
    let shouldObserve = false;

    try {
      const requestOptions = {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      };
      if (this.abortController) requestOptions.signal = this.abortController.signal;

      const response = await fetch(nextUrl, requestOptions);
      if (!response.ok) throw new Error(`Continuous scroll request failed: ${response.status}`);

      const responseDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
      const responseGrid = responseDocument.querySelector(this.dataset.gridSelector);
      if (!responseGrid) throw new Error('Continuous scroll response did not contain the expected grid');

      const responseComponent = responseDocument.querySelector('continuous-scroll');
      const responseNextLink = responseComponent?.querySelector('[data-continuous-scroll-next]');
      const responseNextUrl = responseNextLink?.getAttribute('href');
      if (responseNextUrl === nextUrl) throw new Error('Continuous scroll response repeated the current page');

      const fragment = document.createDocumentFragment();
      Array.from(responseGrid.children).forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(this.grid);
      }

      if (responseNextUrl) {
        this.nextLink.setAttribute('href', responseNextUrl);
        this.setStatus('');
        shouldObserve = true;
      } else {
        this.nextLink.remove();
        this.nextLink = null;
        this.setStatus(this.dataset.completeText);
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.setStatus(this.dataset.errorText);
    } finally {
      this.loading = false;
      this.nextLink?.removeAttribute('aria-disabled');
      this.removeAttribute?.('aria-busy');
      this.abortController = null;
      if (shouldObserve) this.observer?.observe(this);
    }
  }
}

if (!customElements.get('continuous-scroll')) {
  customElements.define('continuous-scroll', ContinuousScroll);
}
